import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import type { AnyExtension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const TABLE_WIDTH_THRESHOLD = 670;

/**
 * Helper function to extract background color from element
 */
const parseBackgroundColor = (element: HTMLElement): string | null => {
  // First check data attribute
  const dataAttr = element.getAttribute('data-background-color');
  if (dataAttr) return dataAttr;

  // Then check inline style
  const style = element.getAttribute('style') || '';
  const bgColorMatch = style.match(/background-color:\s*([^;]+)/i);
  if (bgColorMatch) return bgColorMatch[1].trim();

  const bgMatch = style.match(/background:\s*([^;]+)/i);
  if (bgMatch) {
    const bgValue = bgMatch[1].trim();
    // Only return if it looks like a color (not an image or gradient)
    if (
      bgValue.startsWith('#') ||
      bgValue.startsWith('rgb') ||
      /^[a-z]+$/i.test(bgValue)
    ) {
      return bgValue;
    }
  }

  return null;
};

/**
 * Helper function to extract text alignment from element
 */
const parseTextAlign = (element: HTMLElement): string | null => {
  // Check data attribute first
  const dataAttr = element.getAttribute('data-text-align');
  if (dataAttr) return dataAttr;

  // Check inline style
  const style = element.getAttribute('style') || '';
  const alignMatch = style.match(/text-align:\s*([^;]+)/i);
  if (alignMatch) return alignMatch[1].trim();

  // Check align attribute (legacy HTML)
  const alignAttr = element.getAttribute('align');
  if (alignAttr) return alignAttr;

  return null;
};

/**
 * Helper function to extract vertical alignment from element
 */
const parseVerticalAlign = (element: HTMLElement): string | null => {
  // Check data attribute first
  const dataAttr = element.getAttribute('data-vertical-align');
  if (dataAttr) return dataAttr;

  // Check inline style
  const style = element.getAttribute('style') || '';
  const alignMatch = style.match(/vertical-align:\s*([^;]+)/i);
  if (alignMatch) return alignMatch[1].trim();

  // Check valign attribute (legacy HTML)
  const valignAttr = element.getAttribute('valign');
  if (valignAttr) return valignAttr;

  return null;
};

/**
 * Helper function to extract text color from element
 */
const parseTextColor = (element: HTMLElement): string | null => {
  // Check data attribute first
  const dataAttr = element.getAttribute('data-text-color');
  if (dataAttr) return dataAttr;

  // Check inline style
  const style = element.getAttribute('style') || '';
  const colorMatch = style.match(/(?:^|;)\s*color:\s*([^;]+)/i);
  if (colorMatch) return colorMatch[1].trim();

  return null;
};

/**
 * Plugin to sync cell text color to paragraph nodes within the cell
 */
const createCellColorSyncPlugin = (cellType: 'tableCell' | 'tableHeader') => {
  return new Plugin({
    key: new PluginKey(`${cellType}ColorSync`),
    appendTransaction(transactions, oldState, newState) {
      // Check if any transaction has changes
      const hasChanges = transactions.some((tr) => tr.docChanged);
      if (!hasChanges) return null;

      const { tr } = newState;
      let modified = false;

      // Iterate through the document to find table cells
      newState.doc.descendants((node, pos) => {
        if (node.type.name === cellType && node.attrs.textColor) {
          const cellColor = node.attrs.textColor;

          // Iterate through cell content to find paragraphs
          node.descendants((childNode, childPos) => {
            if (childNode.type.name === 'paragraph') {
              // Check if paragraph needs color style update
              const paragraphPos = pos + 1 + childPos;

              // Check all text nodes in the paragraph for textStyle mark
              childNode.descendants((textNode, textPos) => {
                if (textNode.isText) {
                  const textStyleMark = newState.schema.marks.textStyle;
                  if (textStyleMark) {
                    const existingMark = textNode.marks.find(
                      (m) => m.type === textStyleMark
                    );
                    const existingColor = existingMark?.attrs?.color;

                    // Only apply if no color is set or if it differs
                    if (!existingColor) {
                      const from = paragraphPos + 1 + textPos;
                      const to = from + textNode.nodeSize;
                      const mark = textStyleMark.create({ color: cellColor });
                      tr.addMark(from, to, mark);
                      modified = true;
                    }
                  }
                }
              });
            }
          });
        }
      });

      return modified ? tr : null;
    },
  });
};

/**
 * Custom TableCell with additional attributes
 */
const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (element) => parseBackgroundColor(element),
        renderHTML: (attributes) => {
          return {
            'data-background-color': attributes.backgroundColor || '',
          };
        },
      },
      textAlign: {
        default: 'left',
        parseHTML: (element) => parseTextAlign(element) || 'left',
        renderHTML: (attributes) => {
          return {
            'data-text-align': attributes.textAlign || 'left',
          };
        },
      },
      verticalAlign: {
        default: 'top',
        parseHTML: (element) => parseVerticalAlign(element) || 'top',
        renderHTML: (attributes) => {
          return {
            'data-vertical-align': attributes.verticalAlign || 'top',
          };
        },
      },
      textColor: {
        default: null,
        parseHTML: (element) => parseTextColor(element),
        renderHTML: (attributes) => {
          return {
            'data-text-color': attributes.textColor || '',
          };
        },
      },
      colspan: {
        default: 1,
      },
      rowspan: {
        default: 1,
      },
    };
  },
  addProseMirrorPlugins() {
    return [createCellColorSyncPlugin('tableCell')];
  },
  renderHTML({ node, HTMLAttributes }) {
    const textAlign = node.attrs.textAlign || 'left';
    const verticalAlign = node.attrs.verticalAlign || 'top';
    const backgroundColor = node.attrs.backgroundColor;
    const textColor = node.attrs.textColor;

    let style =
      'border: 1px solid #E2E8F0; padding: 8px 12px; word-wrap: break-word; overflow-wrap: break-word;';
    style += ` text-align: ${textAlign};`;
    style += ` vertical-align: ${verticalAlign};`;
    style += backgroundColor
      ? ` background-color: ${backgroundColor};`
      : ' background: #fff;';
    if (textColor) {
      style += ` color: ${textColor};`;
    }

    return [
      'td',
      {
        ...HTMLAttributes,
        style,
      },
      0,
    ];
  },
});

/**
 * Custom TableHeader with additional attributes
 */
const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (element) => parseBackgroundColor(element),
        renderHTML: (attributes) => {
          return {
            'data-background-color': attributes.backgroundColor || '',
          };
        },
      },
      textAlign: {
        default: 'left',
        parseHTML: (element) => parseTextAlign(element) || 'left',
        renderHTML: (attributes) => {
          return {
            'data-text-align': attributes.textAlign || 'left',
          };
        },
      },
      verticalAlign: {
        default: 'top',
        parseHTML: (element) => parseVerticalAlign(element) || 'top',
        renderHTML: (attributes) => {
          return {
            'data-vertical-align': attributes.verticalAlign || 'top',
          };
        },
      },
      textColor: {
        default: null,
        parseHTML: (element) => parseTextColor(element),
        renderHTML: (attributes) => {
          return {
            'data-text-color': attributes.textColor || '',
          };
        },
      },
      colspan: {
        default: 1,
      },
      rowspan: {
        default: 1,
      },
    };
  },
  addProseMirrorPlugins() {
    return [createCellColorSyncPlugin('tableHeader')];
  },
  renderHTML({ node, HTMLAttributes }) {
    const textAlign = node.attrs.textAlign || 'left';
    const verticalAlign = node.attrs.verticalAlign || 'top';
    const backgroundColor = node.attrs.backgroundColor;
    const textColor = node.attrs.textColor;

    let style =
      'border: 1px solid #E2E8F0; padding: 8px 12px; font-weight: bold; word-wrap: break-word; overflow-wrap: break-word;';
    style += ` text-align: ${textAlign};`;
    style += ` vertical-align: ${verticalAlign};`;
    style += backgroundColor
      ? ` background-color: ${backgroundColor};`
      : ' background-color: #F7FAFC;';
    if (textColor) {
      style += ` color: ${textColor};`;
    }

    return [
      'th',
      {
        ...HTMLAttributes,
        style,
      },
      0,
    ];
  },
});

/**
 * Get all table-related Tiptap extensions
 *
 * This includes:
 * - Table: Main table extension with full functionality
 * - TableRow: Row extension
 * - TableCell: Cell extension with merge/split support
 * - TableHeader: Header cell extension
 *
 * Available commands when table is active:
 * - addRowBefore / addRowAfter: Insert rows
 * - addColumnBefore / addColumnAfter: Insert columns
 * - deleteRow / deleteColumn: Remove rows/columns
 * - deleteTable: Remove entire table
 * - mergeCells / splitCell: Merge or split cells
 * - toggleHeaderRow / toggleHeaderColumn: Toggle headers
 */
/**
 * Custom TableRow with inline styles for email compatibility
 */
const CustomTableRow = TableRow.extend({
  renderHTML({ HTMLAttributes }) {
    return [
      'tr',
      {
        ...HTMLAttributes,
        style: 'border: 1px solid #E2E8F0;',
      },
      0,
    ];
  },
});

/**
 * Custom Table extension that ensures table never exceeds container width
 * Critical for PDF export - prevents content from being cut off
 *
 * When resizing makes the table width exceed 400px, it automatically
 * switches to width: 100% for better responsiveness
 */
const BaseTable = Table.extend({
  renderHTML({ HTMLAttributes }) {
    return [
      'table',
      {
        ...HTMLAttributes,
        class: 'tiptap-table',
      },
      ['tbody', 0],
    ];
  },
});

const CustomTableWithPlugin = BaseTable.extend({
  addProseMirrorPlugins() {
    const parentPlugins = this.parent?.() || [];

    const tableWidthPlugin = new Plugin({
      key: new PluginKey('tableWidthObserver'),
      view() {
        return {
          update(view) {
            // Find all tables in the editor
            const tables = view.dom.querySelectorAll('.editor-style table');

            tables.forEach((table) => {
              const tableElement = table as HTMLTableElement;
              // Get the actual rendered width of the table
              const tableWidth = tableElement.offsetWidth;

              // Check if table has inline min-width style (set by resize)
              const currentMinWidth = tableElement.style.minWidth;
              const minWidthValue = currentMinWidth
                ? parseFloat(currentMinWidth)
                : 0;

              // If the table width or min-width exceeds threshold, set to 100%
              if (
                tableWidth > TABLE_WIDTH_THRESHOLD ||
                minWidthValue > TABLE_WIDTH_THRESHOLD
              ) {
                tableElement.style.minWidth = '100%';
                tableElement.style.width = '100%';
              }
            });
          },
        };
      },
    });

    return [...parentPlugins, tableWidthPlugin];
  },
});

interface TableExtensionsOptions {
  printFormat?: boolean;
}

export const getTableExtensions = (
  options: TableExtensionsOptions = {}
): AnyExtension[] => {
  const { printFormat = false } = options;
  const TableExtension = printFormat ? CustomTableWithPlugin : BaseTable;

  return [
    TableExtension.configure({
      resizable: true,
      allowTableNodeSelection: true,
    }),
    CustomTableRow,
    CustomTableCell,
    CustomTableHeader,
  ];
};

export default getTableExtensions;
